import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import {
    buildNestedGridSvg,
    createDefaultLevelGroup,
    createDefaultVariant,
    randomizeAllVariantParams,
    type FillPattern,
    type RandomizeGridCapMode,
    type LeafCellShape,
    type LeafColorMode,
    type LevelGroup,
    type NestedGridConfig,
    type PackingConfig,
    type VariantConfig,
    type GridShape,
} from "../../utils/nestedGrid";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

// ─── Context interface ────────────────────────────────────────────────────────

interface NestedGridState {
    nestingLevels: number;
    levelGroups: LevelGroup[];
    leafColorMode: LeafColorMode;
    leafFill: string;
    leafCellShape: LeafCellShape;
    /** Gradient stops: smallest -> largest rendered rect (≥2 in UI). */
    leafGradientStops: string[];
    seed: number;
    keepAspectRatio: boolean;
    /** With keepAspectRatio: scale to fit inside parent cell vs cover (extend). */
    containAspectFit: boolean;
    /** rows = columns, cellHeight = cellWidth for all variants when enabled. */
    squareGrid: boolean;
    /** Cap columns/rows when using Randomize (see mode + base in controls). */
    randomizeGridCapMode: RandomizeGridCapMode;
    randomizeGridCapBase: number;
    randomizeGridMinEnabled: boolean;
    randomizeGridMinValue: number;
    mirrorChildrenEnabled: boolean;
    mirrorChildrenPercent: number;
    rotateChildrenEnabled: boolean;
    rotateChildrenPercent: number;
    noSpacing: boolean;
    /** Rotate nested child grids with polar/spiral/hyperbolic tangent (vs axis-aligned in region). */
    polarSpiralEmbedRotation: boolean;
    svgContent: string;

    setNestingLevels: (n: number) => void;
    setLeafColorMode: (m: LeafColorMode) => void;
    setLeafFill: (c: string) => void;
    setLeafCellShape: (s: LeafCellShape) => void;
    setLeafGradientStops: Dispatch<SetStateAction<string[]>>;
    setSeed: (s: number) => void;
    setKeepAspectRatio: (v: boolean) => void;
    setContainAspectFit: (v: boolean) => void;
    setSquareGrid: (v: boolean) => void;
    setRandomizeGridCapMode: (m: RandomizeGridCapMode) => void;
    setRandomizeGridCapBase: (n: number) => void;
    setRandomizeGridMinEnabled: (v: boolean) => void;
    setRandomizeGridMinValue: (n: number) => void;
    setMirrorChildrenEnabled: (v: boolean) => void;
    setMirrorChildrenPercent: (n: number) => void;
    setRotateChildrenEnabled: (v: boolean) => void;
    setRotateChildrenPercent: (n: number) => void;
    setNoSpacing: (v: boolean) => void;
    setPolarSpiralEmbedRotation: (v: boolean) => void;

    // Variant management
    addVariant: (lvl: number) => void;
    removeVariant: (lvl: number, vi: number) => void;
    updateVariant: (
        lvl: number,
        vi: number,
        update: Partial<Omit<VariantConfig, "id" | "packing">>,
    ) => void;
    updatePacking: (
        lvl: number,
        vi: number,
        update: Partial<PackingConfig>,
    ) => void;

    /** Randomizes grid/distribution/packing for every existing variant (ids unchanged). */
    randomizeAllVariants: () => void;

    /** Variant ids skipped by {@link randomizeAllVariants}. */
    lockedVariantIds: ReadonlySet<string>;
    toggleVariantLock: (variantId: string) => void;

    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const NestedGridContext = createContext<NestedGridState | null>(null);

function defaultNestedGridPreset(): Record<string, unknown> {
    return {
        nestingLevels: 2,
        levelGroups: [createDefaultLevelGroup(0), createDefaultLevelGroup(1)],
        leafColorMode: "solid" as LeafColorMode,
        leafFill: "#2a2a2a",
        leafCellShape: "square" as LeafCellShape,
        leafGradientStops: ["#1a237e", "#fff9c4"],
        seed: 1337,
        keepAspectRatio: false,
        containAspectFit: true,
        squareGrid: false,
        randomizeGridCapMode: "none" as RandomizeGridCapMode,
        randomizeGridCapBase: 4,
        randomizeGridMinEnabled: false,
        randomizeGridMinValue: 4,
        mirrorChildrenEnabled: false,
        mirrorChildrenPercent: 50,
        rotateChildrenEnabled: false,
        rotateChildrenPercent: 50,
        noSpacing: false,
        polarSpiralEmbedRotation: true,
    };
}

export function useNestedGrid(): NestedGridState {
    const ctx = useContext(NestedGridContext);
    if (!ctx)
        throw new Error("useNestedGrid must be used within NestedGridProvider");
    return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NestedGridProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [nestingLevels, setNestingLevelsRaw] = useState(2);
    const [levelGroups, setLevelGroups] = useState<LevelGroup[]>([
        createDefaultLevelGroup(0),
        createDefaultLevelGroup(1),
    ]);
    const [leafColorMode, setLeafColorMode] =
        useState<LeafColorMode>("solid");
    const [leafFill, setLeafFill] = useState("#2a2a2a");
    const [leafCellShape, setLeafCellShape] =
        useState<LeafCellShape>("square");
    const [leafGradientStops, setLeafGradientStops] = useState<string[]>([
        "#1a237e",
        "#fff9c4",
    ]);
    const [seed, setSeed] = useState(1337);
    const [keepAspectRatio, setKeepAspectRatio] = useState(false);
    const [containAspectFit, setContainAspectFit] = useState(true);
    const [squareGrid, setSquareGridRaw] = useState(false);
    const [randomizeGridCapMode, setRandomizeGridCapMode] =
        useState<RandomizeGridCapMode>("none");
    const [randomizeGridCapBase, setRandomizeGridCapBase] = useState(4);
    const [randomizeGridMinEnabled, setRandomizeGridMinEnabled] =
        useState(false);
    const [randomizeGridMinValue, setRandomizeGridMinValue] = useState(4);
    const [mirrorChildrenEnabled, setMirrorChildrenEnabled] = useState(false);
    const [mirrorChildrenPercent, setMirrorChildrenPercent] = useState(50);
    const [rotateChildrenEnabled, setRotateChildrenEnabled] = useState(false);
    const [rotateChildrenPercent, setRotateChildrenPercent] = useState(50);
    const [noSpacing, setNoSpacing] = useState(false);
    const [polarSpiralEmbedRotation, setPolarSpiralEmbedRotation] =
        useState(true);
    const [lockedVariantIds, setLockedVariantIds] = useState(
        () => new Set<string>(),
    );

    // ── Nesting level changes ──────────────────────────────────────────────

    const setNestingLevels = useCallback((n: number) => {
        const clamped = Math.max(1, Math.min(5, n));
        setNestingLevelsRaw(clamped);
        setLevelGroups((prev) => {
            if (clamped === prev.length) return prev;
            if (clamped < prev.length) return prev.slice(0, clamped);
            const extra: LevelGroup[] = [];
            for (let i = prev.length; i < clamped; i++) {
                extra.push(createDefaultLevelGroup(i));
            }
            return [...prev, ...extra];
        });
    }, []);

    const setSquareGrid = useCallback((on: boolean) => {
        setSquareGridRaw(on);
        if (on) {
            setLevelGroups((prev) =>
                prev.map((g) => ({
                    variants: g.variants.map((v) => ({
                        ...v,
                        rows: v.columns,
                        cellHeight: v.cellWidth,
                    })),
                })),
            );
        }
    }, []);

    // ── Variant management ─────────────────────────────────────────────────

    const addVariant = useCallback((lvl: number) => {
        setLevelGroups((prev) => {
            const next = prev.map((g, i) => {
                if (i !== lvl) return g;
                return { variants: [...g.variants, createDefaultVariant(lvl)] };
            });
            return next;
        });
    }, []);

    const removeVariant = useCallback((lvl: number, vi: number) => {
        setLevelGroups((prev) => {
            const g = prev[lvl];
            if (!g || g.variants.length <= 1) return prev;
            const removedId = g.variants[vi]?.id;
            const variants = g.variants.filter((_, idx) => idx !== vi);
            if (removedId) {
                setLockedVariantIds((locked) => {
                    if (!locked.has(removedId)) return locked;
                    const next = new Set(locked);
                    next.delete(removedId);
                    return next;
                });
            }
            return prev.map((gr, i) =>
                i === lvl ? { variants } : gr,
            );
        });
    }, []);

    const updateVariant = useCallback(
        (
            lvl: number,
            vi: number,
            update: Partial<Omit<VariantConfig, "id" | "packing">>,
        ) => {
            setLevelGroups((prev) =>
                prev.map((g, i) => {
                    if (i !== lvl) return g;
                    return {
                        variants: g.variants.map((v, j) =>
                            j === vi ? { ...v, ...update } : v,
                        ),
                    };
                }),
            );
        },
        [],
    );

    const updatePacking = useCallback(
        (lvl: number, vi: number, update: Partial<PackingConfig>) => {
            setLevelGroups((prev) =>
                prev.map((g, i) => {
                    if (i !== lvl) return g;
                    return {
                        variants: g.variants.map((v, j) =>
                            j === vi
                                ? { ...v, packing: { ...v.packing, ...update } }
                                : v,
                        ),
                    };
                }),
            );
        },
        [],
    );

    const toggleVariantLock = useCallback((variantId: string) => {
        setLockedVariantIds((prev) => {
            const next = new Set(prev);
            if (next.has(variantId)) next.delete(variantId);
            else next.add(variantId);
            return next;
        });
    }, []);

    const randomizeAllVariants = useCallback(() => {
        setLevelGroups((prev) =>
            randomizeAllVariantParams(prev, {
                squareGrid,
                randomizeGridCapMode,
                randomizeGridCapBase,
                randomizeGridMinEnabled,
                randomizeGridMinValue,
                lockedVariantIds,
            }),
        );
    }, [
        squareGrid,
        randomizeGridCapMode,
        randomizeGridCapBase,
        randomizeGridMinEnabled,
        randomizeGridMinValue,
        lockedVariantIds,
    ]);

    // ── SVG generation ─────────────────────────────────────────────────────

    const nestedConfig = useMemo<NestedGridConfig>(
        () => ({
            nestingLevels,
            levelGroups,
            leafColorMode,
            leafFill,
            leafCellShape,
            leafGradientStops,
            seed,
            keepAspectRatio,
            containAspectFit,
            mirrorChildrenEnabled,
            mirrorChildrenPercent,
            rotateChildrenEnabled,
            rotateChildrenPercent,
            noSpacing,
            polarSpiralEmbedRotation,
        }),
        [
            nestingLevels,
            levelGroups,
            leafColorMode,
            leafFill,
            leafCellShape,
            leafGradientStops,
            seed,
            keepAspectRatio,
            containAspectFit,
            mirrorChildrenEnabled,
            mirrorChildrenPercent,
            rotateChildrenEnabled,
            rotateChildrenPercent,
            noSpacing,
            polarSpiralEmbedRotation,
        ],
    );

    const svgContent = useMemo(
        () => buildNestedGridSvg(nestedConfig),
        [nestedConfig],
    );

    // ── Download ───────────────────────────────────────────────────────────

    const handleDownload = useCallback(() => {
        const blob = new Blob([svgContent], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "nested-grid.svg";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [svgContent]);

    // ── Presets ────────────────────────────────────────────────────────────

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            nestingLevels, levelGroups, leafColorMode, leafFill, leafCellShape,
            leafGradientStops, seed, keepAspectRatio, containAspectFit, squareGrid,
            randomizeGridCapMode, randomizeGridCapBase, randomizeGridMinEnabled,
            randomizeGridMinValue, mirrorChildrenEnabled, mirrorChildrenPercent,
            rotateChildrenEnabled, rotateChildrenPercent, noSpacing, polarSpiralEmbedRotation,
            lockedVariantIds: [...lockedVariantIds],
        }),
        [
            nestingLevels, levelGroups, leafColorMode, leafFill, leafCellShape,
            leafGradientStops, seed, keepAspectRatio, containAspectFit, squareGrid,
            randomizeGridCapMode, randomizeGridCapBase, randomizeGridMinEnabled,
            randomizeGridMinValue, mirrorChildrenEnabled, mirrorChildrenPercent,
            rotateChildrenEnabled, rotateChildrenPercent, noSpacing, polarSpiralEmbedRotation,
            lockedVariantIds,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.nestingLevels != null) setNestingLevelsRaw(values.nestingLevels as number);
        if (values.levelGroups != null) setLevelGroups(values.levelGroups as LevelGroup[]);
        if (values.leafColorMode != null) setLeafColorMode(values.leafColorMode as LeafColorMode);
        if (values.leafFill != null) setLeafFill(values.leafFill as string);
        if (values.leafCellShape != null) setLeafCellShape(values.leafCellShape as LeafCellShape);
        if (values.leafGradientStops != null) setLeafGradientStops(values.leafGradientStops as string[]);
        if (values.seed != null) setSeed(values.seed as number);
        if (values.keepAspectRatio != null) setKeepAspectRatio(values.keepAspectRatio as boolean);
        if (values.containAspectFit != null) setContainAspectFit(values.containAspectFit as boolean);
        if (values.squareGrid != null) setSquareGridRaw(values.squareGrid as boolean);
        if (values.randomizeGridCapMode != null) setRandomizeGridCapMode(values.randomizeGridCapMode as RandomizeGridCapMode);
        if (values.randomizeGridCapBase != null) setRandomizeGridCapBase(values.randomizeGridCapBase as number);
        if (values.randomizeGridMinEnabled != null) setRandomizeGridMinEnabled(values.randomizeGridMinEnabled as boolean);
        if (values.randomizeGridMinValue != null) setRandomizeGridMinValue(values.randomizeGridMinValue as number);
        if (values.mirrorChildrenEnabled != null) setMirrorChildrenEnabled(values.mirrorChildrenEnabled as boolean);
        if (values.mirrorChildrenPercent != null) setMirrorChildrenPercent(values.mirrorChildrenPercent as number);
        if (values.rotateChildrenEnabled != null) setRotateChildrenEnabled(values.rotateChildrenEnabled as boolean);
        if (values.rotateChildrenPercent != null) setRotateChildrenPercent(values.rotateChildrenPercent as number);
        if (values.noSpacing != null) setNoSpacing(values.noSpacing as boolean);
        if (values.polarSpiralEmbedRotation != null) setPolarSpiralEmbedRotation(values.polarSpiralEmbedRotation as boolean);
        if (Array.isArray(values.lockedVariantIds)) {
            setLockedVariantIds(new Set(values.lockedVariantIds as string[]));
        }
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "nested-grid",
        defaultPreset: () => ({
            ...defaultNestedGridPreset(),
            lockedVariantIds: [],
        }),
        getPresetValues,
        applyPreset,
    });

    // ── Context value ──────────────────────────────────────────────────────

    const value: NestedGridState = {
        nestingLevels,
        levelGroups,
        leafColorMode,
        leafFill,
        leafCellShape,
        leafGradientStops,
        seed,
        keepAspectRatio,
        containAspectFit,
        squareGrid,
        randomizeGridCapMode,
        randomizeGridCapBase,
        randomizeGridMinEnabled,
        randomizeGridMinValue,
        mirrorChildrenEnabled,
        mirrorChildrenPercent,
        rotateChildrenEnabled,
        rotateChildrenPercent,
        noSpacing,
        polarSpiralEmbedRotation,
        svgContent,
        setNestingLevels,
        setLeafColorMode,
        setLeafFill,
        setLeafCellShape,
        setLeafGradientStops,
        setSeed,
        setKeepAspectRatio,
        setContainAspectFit,
        setSquareGrid,
        setRandomizeGridCapMode,
        setRandomizeGridCapBase,
        setRandomizeGridMinEnabled,
        setRandomizeGridMinValue,
        setMirrorChildrenEnabled,
        setMirrorChildrenPercent,
        setRotateChildrenEnabled,
        setRotateChildrenPercent,
        setNoSpacing,
        setPolarSpiralEmbedRotation,
        addVariant,
        removeVariant,
        updateVariant,
        updatePacking,
        randomizeAllVariants,
        lockedVariantIds,
        toggleVariantLock,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <NestedGridContext.Provider value={value}>
            {children}
        </NestedGridContext.Provider>
    );
}

// Re-export types needed by controls
export type {
    FillPattern,
    GridShape,
    VariantConfig,
    PackingConfig,
    LevelGroup,
    RandomizeGridCapMode,
    LeafCellShape,
    LeafColorMode,
};
